import React, { useState, useRef, useEffect } from 'react';
import { adminApi } from '../services/api';

// ─── API call ─────────────────────────────────────────────────────────────────
async function uploadClients(formData, onProgress) {
  const token = localStorage.getItem('nb_admin_token');
  return fetch('/api/admin/clients/upload', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  }).then(r => r.json());
}

// Upload history (2026-07-29): bypasses adminApi the same way uploadClients
// above does, using a raw fetch with from/to query params. Matches the
// pattern used for ClientAlerts.jsx's communication history.
function getISTTodayDate() {
  const now    = new Date();
  const istNow = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  return istNow.toISOString().slice(0, 10);
}

async function fetchUploadHistory(from, to) {
  const token  = localStorage.getItem('nb_admin_token');
  const params = new URLSearchParams({ from, to });
  const res  = await fetch(`/api/admin/clients/upload-history?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.json();
}

const REQUIRED_COLS = ['ucc','dob','mobile','email','client_name'];
const OPTIONAL_COLS = ['pan','dp_id','bo_id','account_status','nse_cm','nse_fo','nse_cd','bse_cm','bse_fo','bse_cd','mcx_fo','address','pincode','city','state'];

export default function ClientUpload() {

  const [selectedFile, setSelectedFile] = useState(null);
  const [dragover,     setDragover]     = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [progress,     setProgress]     = useState(0);
  const [result,       setResult]       = useState(null);
  const [preview,      setPreview]      = useState(null);
  const fileInputRef = useRef();

  // ── Upload history state ──
  const today = getISTTodayDate();
  const [history,        setHistory]        = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [fromDate,       setFromDate]       = useState(today);
  const [toDate,         setToDate]         = useState(today);

  const loadHistory = async (from = fromDate, to = toDate) => {
    setHistoryLoading(true);
    try {
      const data = await fetchUploadHistory(from, to);
      setHistory(data.success ? (data.history || []) : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(today, today); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (file) setFile(file);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragover(false);
    const file = e.dataTransfer.files[0];
    if (file) setFile(file);
  }

  function setFile(file) {
    if (!file.name.toLowerCase().endsWith('.csv')) { alert('Only .csv files accepted.'); return; }
    setSelectedFile(file);
    setResult(null);
    // Parse first 5 rows for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result.replace(/^﻿/, '');
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1, 6).map(line => {
        const vals = line.split(',');
        const row = {};
        headers.forEach((h, i) => { row[h] = (vals[i] || '').trim(); });
        return row;
      });
      setPreview({ headers, rows, total: lines.length - 1 });
    };
    reader.readAsText(file);
  }

  async function doUpload() {
    if (!selectedFile) return;
    setUploading(true);
    setProgress(0);
    setResult(null);

    // Simulate progress
    const ticker = setInterval(() => {
      setProgress(p => Math.min(p + 8, 85));
    }, 200);

    const formData = new FormData();
    formData.append('client_file', selectedFile);

    try {
      const data = await uploadClients(formData);
      clearInterval(ticker);
      setProgress(100);
      setTimeout(() => {
        setResult({ success: data.success, message: data.message || data.error, data });
        setUploading(false);
        setProgress(0);
        if (data.success) {
          setSelectedFile(null);
          setPreview(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
          loadHistory(); // refresh so the just-completed upload shows up immediately
        }
      }, 400);
    } catch (err) {
      clearInterval(ticker);
      setResult({ success: false, message: 'Upload failed. Please try again.' });
      setUploading(false);
      setProgress(0);
    }
  }

  function downloadTemplate() {
    const headers = [...REQUIRED_COLS, ...OPTIONAL_COLS].join(',');
    const sample  = '97917850,15-01-1990,9999999999,client@example.com,Client Name,ABCDE1234F,IN300378,12086500,ACTIVE,1,1,0,1,1,0,1,Address Here,600002,Chennai,Tamil Nadu';
    const blob    = new Blob([headers + '\n' + sample], { type: 'text/csv' });
    const url     = URL.createObjectURL(blob);
    const a       = document.createElement('a');
    a.href = url; a.download = 'client_upload_template.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ fontFamily: "'DM Sans',-apple-system,sans-serif" }}>

      {/* ── Template Download ── */}
      <div style={s.infoBar}>
        <div>
          <div style={s.infoTitle}>CSV Upload Format</div>
          <div style={s.infoSub}>
            <strong>Required:</strong> {REQUIRED_COLS.join(', ')}<br />
            <strong>Optional:</strong> {OPTIONAL_COLS.join(', ')}<br />
            <strong>Date format:</strong> DD-MM-YYYY &nbsp;·&nbsp;
            <strong>Bit fields (0/1):</strong> nse_cm, nse_fo, nse_cd, bse_cm, bse_fo, bse_cd, mcx_fo
          </div>
        </div>
        <button style={s.downloadBtn} onClick={downloadTemplate}>
          ⬇ Download Template
        </button>
      </div>

      {/* ── Upload Card ── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <div style={s.cardTitle}>Upload Client CSV</div>
          <div style={s.cardSub}>Bulk add or update clients from a CSV file</div>
        </div>
        <div style={s.cardBody}>

          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragover(true); }}
            onDragLeave={() => setDragover(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            style={{
              ...s.dropZone,
              borderColor: dragover ? '#2563eb' : selectedFile ? '#16a34a' : '#e2e8f0',
              background:  dragover ? '#eff6ff' : selectedFile ? '#f0fdf4' : '#fafafa',
            }}
          >
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />
            <div style={{ fontSize: '28px', marginBottom: '8px' }}>{selectedFile ? '📄' : '📂'}</div>
            {selectedFile ? (
              <>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#15803d', marginBottom: '3px' }}>{selectedFile.name}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>{(selectedFile.size / 1024).toFixed(1)} KB · Click to change</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '3px' }}>Drop CSV file here or click to browse</div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>Only .csv files · Max 10 MB</div>
              </>
            )}
          </div>

          {/* Preview */}
          {preview && (
            <div style={s.preview}>
              <div style={s.previewTitle}>Preview — first {Math.min(5, preview.rows.length)} of {preview.total} rows</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={s.previewTable}>
                  <thead>
                    <tr>
                      {preview.headers.slice(0, 7).map(h => (
                        <th key={h} style={s.previewTh}>{h}</th>
                      ))}
                      {preview.headers.length > 7 && <th style={s.previewTh}>+{preview.headers.length - 7} more</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, i) => (
                      <tr key={i}>
                        {preview.headers.slice(0, 7).map(h => (
                          <td key={h} style={s.previewTd}>{row[h] || '—'}</td>
                        ))}
                        {preview.headers.length > 7 && <td style={s.previewTd}>...</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Progress */}
          {uploading && (
            <div style={{ marginBottom: '12px' }}>
              <div style={s.progressBg}>
                <div style={{ ...s.progressBar, width: `${progress}%` }} />
              </div>
              <div style={s.progressLabel}>
                {progress < 40 ? 'Uploading...' : progress < 80 ? 'Processing clients...' : 'Finalising...'} {progress}%
              </div>
            </div>
          )}

          {/* Upload button */}
          <button
            onClick={doUpload}
            disabled={!selectedFile || uploading}
            style={{ ...s.uploadBtn, background: !selectedFile || uploading ? '#93c5fd' : '#2563eb', cursor: !selectedFile || uploading ? 'not-allowed' : 'pointer' }}
          >
            {uploading ? '⏳ Processing...' : '⬆ Upload & Process Clients'}
          </button>

          {/* Result */}
          {result && (
            <div style={{ ...s.resultBox, background: result.success ? '#f0fdf4' : '#fef2f2', border: `0.5px solid ${result.success ? '#bbf7d0' : '#fecaca'}` }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: result.success ? '#15803d' : '#dc2626', marginBottom: result.success ? '10px' : 0 }}>
                {result.success ? '✓ ' : '✗ '}{result.message}
              </div>
              {result.success && result.data && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '8px' }}>
                  {[
                    { label: 'Total Rows', value: result.data.total_rows,  color: '#374151' },
                    { label: 'Inserted',   value: result.data.inserted,    color: '#15803d' },
                    { label: 'Updated',    value: result.data.updated,     color: '#1d4ed8' },
                    { label: 'Skipped',    value: result.data.skipped,     color: '#d97706' },
                  ].map(stat => (
                    <div key={stat.label} style={s.statBox}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: stat.color }}>{stat.value ?? 0}</div>
                      <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Upload History ── */}
      <div style={{ marginTop: '20px' }}>

        {/* Date range bar */}
        <div style={s.historyDateBar}>
          <div>
            <div style={s.dateLabel}>From</div>
            <input type="date" value={fromDate} max={toDate}
              onChange={e => setFromDate(e.target.value)}
              style={s.dateInput} />
          </div>
          <div>
            <div style={s.dateLabel}>To</div>
            <input type="date" value={toDate} min={fromDate}
              onChange={e => setToDate(e.target.value)}
              style={s.dateInput} />
          </div>
          <button onClick={() => loadHistory(fromDate, toDate)} style={s.applyBtn}>Apply</button>
          <button onClick={() => { setFromDate(today); setToDate(today); loadHistory(today, today); }} style={s.todayBtn}>Today</button>
          <div style={{ marginLeft: 'auto', fontSize: '11px', color: '#94a3b8' }}>
            {history.length} upload{history.length !== 1 ? 's' : ''} in range
          </div>
        </div>

        <div style={s.card}>
          <div style={s.cardHead}>
            <div style={s.cardTitle}>Upload History</div>
            <div style={s.cardSub}>Every CSV bulk-upload run, who ran it, and the resulting counts</div>
          </div>

          {/* Header row */}
          <div style={s.historyHeaderRow}>
            {['Filename', 'Uploaded By', 'Uploaded At', 'Total Rows', 'Inserted', 'Updated', 'Skipped'].map(h => (
              <div key={h} style={s.historyHeaderCell}>{h}</div>
            ))}
          </div>

          {historyLoading ? (
            <div style={s.historyEmpty}>Loading...</div>
          ) : history.length === 0 ? (
            <div style={s.historyEmpty}>No uploads in this date range.</div>
          ) : (
            history.map((h, i) => (
              <div key={h.id} style={{ ...s.historyRow, background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <div style={{ ...s.historyCell, fontWeight: 500, color: '#0f172a' }}>{h.filename}</div>
                <div style={s.historyCell}>{h.uploaded_by || '—'}</div>
                <div style={s.historyCell}>
                  {new Date(h.uploaded_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
                <div style={s.historyCell}>{h.total_rows}</div>
                <div style={{ ...s.historyCell, color: '#15803d', fontWeight: 600 }}>{h.inserted}</div>
                <div style={{ ...s.historyCell, color: '#1d4ed8', fontWeight: 600 }}>{h.updated}</div>
                <div style={{ ...s.historyCell, color: '#d97706', fontWeight: 600 }}>{h.skipped}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  infoBar:      { background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: '10px', padding: '14px 18px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' },
  infoTitle:    { fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '6px' },
  infoSub:      { fontSize: '11.5px', color: '#64748b', lineHeight: '1.8' },
  downloadBtn:  { background: '#eff6ff', border: '0.5px solid #bfdbfe', color: '#1d4ed8', padding: '7px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' },
  card:         { background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' },
  cardHead:     { padding: '14px 18px', borderBottom: '0.5px solid #e2e8f0' },
  cardTitle:    { fontSize: '13px', fontWeight: '600', color: '#0f172a', marginBottom: '2px' },
  cardSub:      { fontSize: '11px', color: '#64748b' },
  cardBody:     { padding: '18px' },
  dropZone:     { border: '2px dashed', borderRadius: '10px', padding: '28px 20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s', marginBottom: '14px' },
  preview:      { background: '#f8fafc', border: '0.5px solid #e2e8f0', borderRadius: '8px', padding: '12px', marginBottom: '14px' },
  previewTitle: { fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' },
  previewTable: { width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' },
  previewTh:    { background: '#f1f5f9', padding: '6px 10px', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '10px', textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' },
  previewTd:    { padding: '6px 10px', color: '#374151', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' },
  progressBg:   { background: '#f3f4f6', borderRadius: '99px', height: '6px', overflow: 'hidden', marginBottom: '5px' },
  progressBar:  { background: '#2563eb', height: '100%', borderRadius: '99px', transition: 'width 0.3s' },
  progressLabel:{ fontSize: '11px', color: '#6b7280', textAlign: 'center', marginBottom: '12px' },
  uploadBtn:    { width: '100%', height: '42px', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  resultBox:    { borderRadius: '8px', padding: '14px 16px', marginTop: '14px' },
  statBox:      { background: '#fff', borderRadius: '6px', padding: '8px 10px', textAlign: 'center' },

  // Upload history additions
  historyDateBar:    { background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: '10px', padding: '14px 18px', marginBottom: '14px', display: 'flex', alignItems: 'flex-end', gap: '14px', flexWrap: 'wrap' },
  dateLabel:         { fontSize: '10px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px' },
  dateInput:         { fontSize: '12px', padding: '6px 10px', borderRadius: '6px', border: '0.5px solid #e2e8f0', fontFamily: 'inherit' },
  applyBtn:          { background: '#2563eb', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  todayBtn:          { background: 'transparent', color: '#64748b', border: '0.5px solid #e2e8f0', padding: '7px 14px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' },
  historyHeaderRow:  { display: 'grid', gridTemplateColumns: '1.6fr 1.2fr 1.3fr 0.8fr 0.8fr 0.8fr 0.8fr', padding: '10px 18px', background: '#f8fafc', borderBottom: '0.5px solid #e2e8f0' },
  historyHeaderCell: { fontSize: '10px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  historyRow:        { display: 'grid', gridTemplateColumns: '1.6fr 1.2fr 1.3fr 0.8fr 0.8fr 0.8fr 0.8fr', padding: '11px 18px', alignItems: 'center', borderBottom: '0.5px solid #f1f5f9' },
  historyCell:       { fontSize: '12px', color: '#374151' },
  historyEmpty:      { padding: '32px', textAlign: 'center', fontSize: '12px', color: '#94a3b8' },
};